/**
 * MONSTER TOWN — TOWN SHOP ART. OWNED BY THE VENUE/ECONOMY CREW (src/venue/**).
 *
 * WHAT WAS WRONG.
 *
 * The shop a kid walks into to spend their coins was twelve identical cream
 * plinths standing on a checkerboard, several of them empty, each with a row of
 * tiny gold dots on its front face. No shelves, no counter, no signage, nobody
 * behind the till. It read as a warehouse of placeholder pedestals rather than
 * as the room where the whole economy pays off, and the prices — the one number
 * in the game that decides whether a kid can have the thing — were about ten
 * pixels tall.
 *
 * WHAT IT IS NOW.
 *
 *  - A shelf is a piece of FURNITURE: a wooden crate-stand with a back board, a
 *    coloured awning strip and a lit shelf, with the goods sitting on it.
 *  - The price is a proper hanging tag with COUNTABLE coins on it (see
 *    `coinPrice`, which now lays out in rows of five instead of one long smear).
 *  - Owned things wear a big green tick and swap their awning to green, so the
 *    shelf says "you already have this" from across the room.
 *  - The room gets a counter, a hanging sign and a shopkeeper (built in
 *    `src/economy/shop.ts` from the one shared monster factory).
 *
 * Same builder contract as everywhere: origin at the footprint centre, floor at
 * y = 0, deterministic, kit materials only, ink on silhouettes only.
 */

import * as THREE from 'three'
import type { ArtContext } from '../core/placeable'
import { requirePlaceable } from '../core/placeable'
import { css, type PaletteName } from '../core/palette'
import type { PlaceableId } from '../core/types'
import { ball, boxOn, cylinder, generatedTexture, group, inked, planeGeo } from '../render/kit'

const FALLBACK_OFFER = 'block_wood' as PlaceableId

// ---------------------------------------------------------------------------
// THE PRICE TAG — one draw call, not thirty
// ---------------------------------------------------------------------------

/**
 * A PRICE IS A PICTURE, AND A PICTURE IS ONE QUAD.
 *
 * The 3D `coinPrice` builds two meshes per coin (a gold disc and an ink rim
 * behind it, so neighbouring coins can be told apart and COUNTED). That is the
 * right look and the wrong cost: a 45-coin price is nine coins is eighteen
 * meshes, times fifteen shelves, and the town shop measured 714 draw calls on
 * the older iPad — half of them price coins. Well over what a hand-me-down can
 * hold a frame with.
 *
 * Drawn into a 128px canvas instead, a whole price is ONE textured quad. The
 * discs are identical in meaning — big = 100, middle = 10, little = 1, rows of
 * five, ink rim behind each so they never merge into a gold sausage — and they
 * are actually CRISPER small, because a canvas circle beats a 12-segment
 * cylinder at 20 screen pixels. Textures and materials are cached by amount, and
 * a shop only ever shows a couple of dozen distinct prices.
 */
const COINS_PER_ROW = 5
const priceMaterials = new Map<number, THREE.MeshBasicMaterial>()

function priceMaterial(amount: number): THREE.MeshBasicMaterial {
  const found = priceMaterials.get(amount)
  if (found !== undefined) return found
  const tex = generatedTexture(`shopPrice:${amount}`, (c, s) => {
    c.clearRect(0, 0, s, s)
    /*
     * A WARM TAG, NOT A SHEET OF PAPER (venue fix, r3).
     *
     * `uiPanel` is pure white, and fifteen white squares on a wooden counter
     * with a few tiny gold dots on them is what the round-3 critic photographed:
     * "rows of blank white price cards... it reads as a warehouse of blank paper
     * slips rather than a shop". Cream card stock and a fat ink rim make it a
     * price TAG, and the coins below are drawn a good deal bigger so what is on
     * it is the countable thing rather than the card.
     */
    const pad = s * 0.05
    c.fillStyle = css('uiPanelAlt')
    c.strokeStyle = css('ink')
    c.lineWidth = s * 0.075
    const r = s * 0.18
    c.beginPath()
    c.moveTo(pad + r, pad)
    c.arcTo(s - pad, pad, s - pad, s - pad, r)
    c.arcTo(s - pad, s - pad, pad, s - pad, r)
    c.arcTo(pad, s - pad, pad, pad, r)
    c.arcTo(pad, pad, s - pad, pad, r)
    c.closePath()
    c.fill()
    c.stroke()
    // A gold strip across the top. It is what stops a row of these reading as
    // blank slips of paper from across the room: every tag now says COINS
    // before you are close enough to count them.
    c.save()
    c.clip()
    c.fillStyle = css('uiCoin')
    c.fillRect(0, 0, s, s * 0.2)
    c.restore()
    c.stroke()

    const bands: { r: number; n: number; fill: string }[] = [
      { r: s * 0.135, n: Math.min(Math.floor(amount / 100), 5), fill: css('uiCoin') },
      { r: s * 0.105, n: Math.min(Math.floor((amount % 100) / 10), 9), fill: css('uiCoin') },
      { r: s * 0.075, n: Math.min(amount % 10, 9), fill: css('uiCoinDark') },
    ]
    const lines: { r: number; n: number; fill: string }[] = []
    for (const b of bands) {
      for (let left = b.n; left > 0; left -= COINS_PER_ROW) {
        lines.push({ r: b.r, n: Math.min(COINS_PER_ROW, left), fill: b.fill })
      }
    }
    if (lines.length === 0) return
    const gap = s * 0.035
    let h = 0
    for (const l of lines) h += l.r * 2 + gap
    let y = s / 2 - h / 2 + (lines[0]?.r ?? 0)
    for (const l of lines) {
      const step = l.r * 2.45
      const x0 = s / 2 - ((l.n - 1) * step) / 2
      for (let i = 0; i < l.n; i++) {
        c.beginPath()
        c.arc(x0 + i * step, y, l.r, 0, Math.PI * 2)
        c.fillStyle = l.fill
        c.strokeStyle = css('ink')
        c.lineWidth = s * 0.022
        c.fill()
        c.stroke()
      }
      y += l.r * 2 + gap
    }
  })
  // `alphaTest`, not `transparent`. A transparent quad with `depthWrite: false`
  // is sorted against everything else in the room and drew OVER the crate it is
  // supposed to be stuck to; cutting the alpha instead keeps it an ordinary
  // opaque surface that occludes and is occluded correctly.
  const mat = new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.5 })
  priceMaterials.set(amount, mat)
  return mat
}

/**
 * The price tag: one quad, standing up on the front of the shelf.
 *
 * `planeGeo` is the kit's FLOOR plane — it is pre-rotated flat so terrain and
 * rugs can use it directly. Left as-is, fifteen price tags lay face-up on the
 * floor in front of their stalls like dropped receipts. Standing it back up is
 * a quarter turn about X.
 */
function priceTag(amount: number, size: number): THREE.Mesh {
  const m = new THREE.Mesh(planeGeo(size, size), priceMaterial(amount))
  m.rotation.x = Math.PI / 2
  m.name = 'price'
  return m
}

/**
 * Awning colours, picked deterministically from the id of the thing on sale.
 *
 * Twelve identical cream boxes is a wall; twelve boxes with four different
 * awnings is a row of stalls. The pick is stable for a given offer, so a shelf
 * does not change colour when the shop restocks around it.
 */
const AWNINGS: readonly PaletteName[] = ['uiAccent', 'uiPrimary', 'wallLilac', 'roofTeal']

function awningFor(offer: string): PaletteName {
  let h = 0
  for (let i = 0; i < offer.length; i++) h = (h * 31 + offer.charCodeAt(i)) >>> 0
  return AWNINGS[h % AWNINGS.length] ?? 'uiAccent'
}

/**
 * ONE SHELF IN THE TOWN SHOP.
 *
 * What it sells lives in the INSTANCE STATE, so the shop restocks itself as
 * tiers unlock without a single engine change: this renders whatever def id it
 * was handed, at thumbnail scale, with its price in coin discs on the tag.
 */
export function shopShelfArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('shopShelf')
  const state = (ctx.instance?.state ?? {}) as { offer?: unknown; cost?: unknown; owned?: unknown }
  const offer = (typeof state.offer === 'string' ? state.offer : FALLBACK_OFFER) as PlaceableId
  const cost = typeof state.cost === 'number' ? state.cost : 0
  const owned = state.owned === true
  const awning = owned ? 'uiOk' : awningFor(String(offer))

  // -- the stand: a wooden crate with a lighter shelf board on top ----------
  //
  // DRAW-CALL DISCIPLINE. Fifteen of these stand in one room on a hand-me-down
  // iPad, so every part here is one that changes the silhouette. The front
  // slats, the scalloped awning edge and three of the five ink outlines were
  // cut after this room measured 714 draw calls; none of them survived the
  // "would a three-year-old notice" test, and the awning colour does all the
  // work of telling one stall from the next.
  const crate = boxOn(t * 0.82, t * 0.6, t * 0.7, { color: 'wood', name: 'column' })
  g.add(inked(crate))
  const shelf = boxOn(t * 0.92, t * 0.09, t * 0.8, { color: 'woodLight', y: t * 0.6, name: 'shelf' })
  g.add(shelf)

  // -- the back board and its awning: this is what makes it a STALL ---------
  const back = boxOn(t * 0.86, t * 0.72, t * 0.07, { color: 'wallCream', y: t * 0.69, z: -t * 0.33, name: 'back' })
  g.add(back)
  const stripe = boxOn(t * 0.94, t * 0.2, t * 0.14, { color: awning, y: t * 1.41, z: -t * 0.33, name: 'awning' })
  g.add(inked(stripe))

  /*
   * -- the goods, rendered from the real def so the shop can never lie ------
   *
   * AND MADE TO READ, WHATEVER SHAPE THEY ARE (venue fix, r3).
   *
   * Scaling by the biggest of the three axes is right for a chair and useless
   * for a floor tile, a path piece or a rug: those are wide and 4cm thick, so
   * they came out as a wafer lying on the shelf, and the round-3 critic counted
   * "maybe five actual objects among them" across two full counters of stalls.
   * Flat stock is now stood UP on a little easel — the way a real shop displays
   * a tile — and everything is sized against a target HEIGHT with a floor under
   * it, so no offer can render as nothing.
   */
  const thumb = group('thumb')
  thumb.position.y = t * 0.69
  try {
    const def = requirePlaceable(offer)
    const sub = def.buildArt({ ...ctx, def, instance: null, ghost: true, connect: 0, connect8: 0, tint: null })
    const box = new THREE.Box3().setFromObject(sub)
    const size = box.getSize(new THREE.Vector3())
    const flat = size.y < Math.max(size.x, size.z) * 0.34
    const holder = group('display')
    if (flat) {
      // Tipped back against a stand so the face a kid recognises is the face
      // pointing at the camera.
      const k = (t * 0.78) / Math.max(size.x, size.z, 0.001)
      sub.scale.setScalar(k)
      sub.position.y = -box.min.y * k
      const tilt = group('easel')
      tilt.rotation.x = -1.15
      tilt.add(sub)
      holder.add(tilt)
      holder.position.y = t * 0.12
      thumb.add(boxOn(t * 0.5, t * 0.06, t * 0.16, { color: 'woodDark' }))
    } else {
      const k = (t * 0.74) / Math.max(size.x, size.y, size.z, 0.001)
      sub.scale.setScalar(k)
      sub.position.y = -box.min.y * k
      holder.add(sub)
    }
    thumb.add(holder)
  } catch {
    thumb.add(ball(t * 0.24, { color: 'uiDisabled', y: t * 0.24, seg: 8 }))
  }
  g.add(thumb)

  // -- the price tag --------------------------------------------------------
  //
  // MOUNTED FLAT ON THE FRONT OF THE STALL, not hung out in front of it. Hung
  // on a bracket, a one-quad tag has no thickness to catch the light and reads
  // as a sheet of white paper floating in mid-air — the art contract forbids a
  // billboard here (a builder must be deterministic for a given ArtContext,
  // and the camera can be at any of four rotations when it runs), so the tag
  // sits on the crate's front face where the fixed iso camera always sees it at
  // the same friendly angle. `price` is the name the shop's "not enough yet"
  // jiggle looks for.
  // Smaller than it was, and lower: a tag stuck on the crate, not a poster
  // covering it. The coins on it are drawn bigger to compensate, so the number
  // is easier to count than it was when the card was half the stall wide.
  const holder = group('price')
  holder.position.set(0, t * 0.2, t * 0.365)
  holder.add(priceTag(cost, t * 0.34))
  g.add(holder)

  if (owned) {
    // a fat tick so "you already have this" needs no words
    const tick = group('tick')
    tick.position.set(0, t * 1.0, t * 0.2)
    const a = boxOn(t * 0.12, t * 0.26, t * 0.07, { color: 'uiOk' })
    a.rotation.z = 0.7
    a.position.set(-t * 0.1, 0, 0)
    const b = boxOn(t * 0.12, t * 0.46, t * 0.07, { color: 'uiOk' })
    b.rotation.z = -0.5
    b.position.set(t * 0.07, t * 0.07, 0)
    tick.add(a, b)
    g.add(tick)
  }
  return g
}

/**
 * THE SHOP COUNTER — a till to stand behind.
 *
 * A shop with nobody serving in it is a storeroom. This is where the shopkeeper
 * stands (see `installShopKeeper`), and it is the one piece of furniture in the
 * room that is not a shelf, which is what stops the floor reading as a grid of
 * identical boxes.
 */
export function shopCounterArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('shopCounter')
  const body = boxOn(t * 1.9, t * 0.76, t * 0.8, { color: 'wallLilac', name: 'body' })
  g.add(inked(body))
  g.add(boxOn(t * 2.02, t * 0.11, t * 0.92, { color: 'woodLight', y: t * 0.76, name: 'top' }))
  // panelled front
  for (const s of [-1, 1]) {
    g.add(boxOn(t * 0.72, t * 0.42, t * 0.05, { color: 'uiPanelAlt', x: s * t * 0.46, y: t * 0.18, z: t * 0.42 }))
  }
  // the till, with a coin drawer that reads at a glance
  const till = boxOn(t * 0.56, t * 0.34, t * 0.44, { color: 'metal', x: t * 0.5, y: t * 0.87, name: 'till' })
  g.add(inked(till))
  g.add(boxOn(t * 0.38, t * 0.16, t * 0.05, { color: 'glassDark', x: t * 0.5, y: t * 1.06, z: t * 0.22 }))
  const drawer = boxOn(t * 0.5, t * 0.12, t * 0.3, { color: 'uiCoin', x: t * 0.5, y: t * 0.8, z: t * 0.24 })
  g.add(drawer)
  // a jar of coins on the other end, so the counter says MONEY without words
  const jar = cylinder(t * 0.16, t * 0.26, { color: 'glass', x: -t * 0.56, y: t * 0.87, seg: 12 })
  g.add(jar)
  for (let i = 0; i < 3; i++) {
    const c = cylinder(t * 0.11, t * 0.04, { color: 'uiCoin', x: -t * 0.56, y: t * (0.9 + i * 0.06), seg: 12 })
    g.add(c)
  }
  return g
}

/**
 * THE HANGING SIGN — a big coin over the counter. LAW #4: the room says what it
 * is with a picture, and it is the same coin the purse in the corner uses, so a
 * kid who has seen their coins go up already knows what this room is for.
 */
export function shopSignArt(t: number): THREE.Group {
  const g = group('shopSign')
  // two chains up to the ceiling, two links each
  for (const s of [-1, 1]) {
    for (let i = 0; i < 2; i++) {
      g.add(ball(t * 0.06, { color: 'metalDark', x: s * t * 0.5, y: t * (1.56 + i * 0.22), seg: 6 }))
    }
  }
  const board = boxOn(t * 1.5, t * 0.62, t * 0.12, { color: 'uiPanel', y: t * 0.9, name: 'board' })
  g.add(inked(board))
  g.add(boxOn(t * 1.6, t * 0.1, t * 0.16, { color: 'uiPrimary', y: t * 1.52 }))
  const disc = cylinder(t * 0.24, t * 0.06, { color: 'uiCoin', y: t * 1.2, z: t * 0.1, seg: 16 })
  disc.rotation.x = Math.PI / 2
  g.add(inked(disc))
  const inner = cylinder(t * 0.14, t * 0.08, { color: 'uiCoinDark', y: t * 1.2, z: t * 0.12, seg: 14 })
  inner.rotation.x = Math.PI / 2
  g.add(inner)
  // a little shopping bag beside the coin: "things you can take home"
  g.add(boxOn(t * 0.3, t * 0.3, t * 0.06, { color: 'uiPrimary', x: t * 0.44, y: t * 1.04, z: t * 0.1 }))
  g.add(cylinder(t * 0.02, t * 0.16, { color: 'ink', x: t * 0.44, y: t * 1.34, z: t * 0.1, seg: 5 }))
  return g
}

/**
 * WALL SHELVING behind the counter — stacked crates and jars along the back
 * wall. It never has to be interactive because it is not a placeable: it is part
 * of the room's shell, which is exactly why the room can afford it (one build,
 * no picking, no state).
 */
export function shopWallUnitArt(t: number, seed: number): THREE.Group {
  const g = group('shopWallUnit')
  const frame = boxOn(t * 2.4, t * 2.0, t * 0.34, { color: 'wood', name: 'frame' })
  g.add(inked(frame))
  const tints: PaletteName[] = ['wallMint', 'wallPink', 'wallBlue', 'uiPanelAlt', 'wallCream']
  // Three shelves of three, not four — dressing, not stock, and it stands
  // behind everything a kid actually taps.
  for (let row = 0; row < 3; row++) {
    g.add(boxOn(t * 2.3, t * 0.08, t * 0.4, { color: 'woodLight', y: t * (0.5 + row * 0.55), z: t * 0.02 }))
    for (let i = 0; i < 3; i++) {
      const k = (seed + row * 7 + i * 3) % tints.length
      g.add(
        boxOn(t * 0.56, t * 0.34, t * 0.28, {
          color: tints[k] ?? 'wallCream',
          x: (i - 1) * t * 0.7,
          y: t * (0.58 + row * 0.55),
          z: t * 0.04,
        }),
      )
    }
  }
  return g
}
